
import React, { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useBoards } from "@/contexts/boards";
import AddBoardButton from "@/components/boards/AddBoardButton";
import { useDragAndDrop } from "@/hooks/useDragAndDrop";
import { SidebarMenu } from "@/components/ui/sidebar/components/menu";
import { SidebarGroupContent } from "@/components/ui/sidebar/components/SidebarGroup";
import SidebarSection from "@/components/ui/sidebar/components/SidebarSection";
import BoardList from "@/components/boards/BoardList";
import EditBoardDialog from "@/components/boards/EditBoardDialog";
import DeleteBoardDialog from "@/components/boards/DeleteBoardDialog";

const SidebarBoardsSection = () => {
  const location = useLocation();
  const { 
    boards, 
    isLoading, 
    startEditBoard, 
    startDeleteBoard,
    getBoardById,
    refreshBoards,
    updateBoardPosition
  } = useBoards();
  
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dropTargetId, setDropTargetId] = useState<string | null>(null);
  const [contentDropTargetId, setContentDropTargetId] = useState<string | null>(null);
  
  const [isBoardsOpen, setIsBoardsOpen] = useState(() => {
    const savedState = localStorage.getItem('sidebar-boards-open');
    return savedState !== null ? savedState === 'true' : true;
  });
  
  const { 
    handleDragOver, 
    handleDropOnBoard,
    isDragging: isContentDragging
  } = useDragAndDrop();

  useEffect(() => {
    localStorage.setItem('sidebar-boards-open', isBoardsOpen.toString());
  }, [isBoardsOpen]);

  useEffect(() => {
    console.log("SidebarBoardsSection mounted or route changed, refreshing boards");
    refreshBoards();
  }, [refreshBoards, location.pathname]);

  const handleEdit = useCallback((id: string) => {
    const board = getBoardById(id);
    if (board) {
      startEditBoard(board);
    } else {
      console.error(`Board with id ${id} not found for editing`);
    }
  }, [getBoardById, startEditBoard]);

  const handleDelete = useCallback((id: string) => {
    const board = getBoardById(id);
    if (board) {
      startDeleteBoard(board);
    } else {
      console.error(`Board with id ${id} not found for deletion`);
    }
  }, [getBoardById, startDeleteBoard]);
  
  const handleDragStart = (e: React.DragEvent, id: string) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', id);
    setDraggingId(id);
    setIsDragging(true);
    
    setTimeout(() => {
      setIsDragging(true);
    }, 0);
  };
  
  const handleBoardDragOver = (e: React.DragEvent, targetId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    
    // Improved detection for content drag operations
    let isContentDrag = false;
    
    try {
      // Check for content drag operation (will throw if no app/json data)
      const jsonData = e.dataTransfer.getData('application/json');
      if (jsonData) {
        isContentDrag = true;
        setContentDropTargetId(targetId);
        return;
      }
    } catch (err) {
      // If getData throws, check the external state flag
      if (isContentDragging) {
        isContentDrag = true;
        setContentDropTargetId(targetId);
        return;
      }
    }
    
    // If not content drag, handle board reordering
    if (!isContentDrag && targetId !== draggingId) {
      setDropTargetId(targetId);
    } else {
      setDropTargetId(null);
    }
  };
  
  const handleDragLeave = () => {
    setDropTargetId(null);
    setContentDropTargetId(null);
  };
  
  const handleDrop = async (e: React.DragEvent, targetId: string) => {
    e.preventDefault();
    console.log("Drop event on board:", targetId);
    
    // Improved content drop detection
    let isContentDrop = false;
    
    try {
      // First try to get JSON data for content drops
      const jsonData = e.dataTransfer.getData('application/json');
      if (jsonData) {
        isContentDrop = true;
        console.log("Content drop detected on board:", targetId);
        handleDropOnBoard(e, targetId);
        setContentDropTargetId(null);
        return;
      }
    } catch (err) {
      console.log("Error checking for JSON data:", err);
      // If we're in a content dragging state (tracked externally), assume content drop
      if (isContentDragging) {
        isContentDrop = true;
        console.log("Content drop detected via state flag on board:", targetId);
        handleDropOnBoard(e, targetId);
        setContentDropTargetId(null);
        return;
      }
    }
    
    // If not a content drop, handle board reordering
    if (!isContentDrop) {
      const sourceId = e.dataTransfer.getData('text/plain');
      
      if (sourceId === targetId) {
        setDraggingId(null);
        setIsDragging(false);
        setDropTargetId(null);
        return;
      }
      
      const sourceBoard = getBoardById(sourceId);
      const targetBoard = getBoardById(targetId);
      
      if (sourceBoard && targetBoard) {
        console.log(`Moving board ${sourceId} to order ${targetBoard.display_order}`);
        await updateBoardPosition(sourceId, targetBoard.display_order);
      }
    }
    
    setDraggingId(null);
    setIsDragging(false);
    setDropTargetId(null);
  };
  
  const handleDragEnd = () => {
    setDraggingId(null);
    setIsDragging(false);
    setDropTargetId(null);
    setContentDropTargetId(null);
  };

  const handleContentDragOverWithState = (e: React.DragEvent, id: string) => {
    handleDragOver(e);
    console.log("Content being dragged over board:", id);
    setContentDropTargetId(id);
  };
  
  const handleContentDrop = (e: React.DragEvent, id: string) => {
    console.log("Content dropped on board:", id);
    handleDropOnBoard(e, id);
    setContentDropTargetId(null);
  };

  return (
    <div className="w-full">
      <SidebarSection 
        title="Boards"
        isOpen={isBoardsOpen}
        onOpenChange={setIsBoardsOpen}
        action={
          <AddBoardButton 
            variant="circle"
            showHoverText={true}
          />
        }
      >
        <SidebarGroupContent className="py-0">
          <SidebarMenu>
            <BoardList
              boards={boards}
              isLoading={isLoading}
              draggingId={draggingId}
              dropTargetId={dropTargetId}
              contentDropTargetId={contentDropTargetId}
              onDragStart={handleDragStart}
              onDragOver={handleBoardDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onDragEnd={handleDragEnd}
              onContentDragOver={handleContentDragOverWithState}
              onContentDrop={handleContentDrop}
              onEdit={handleEdit}
              onDelete={handleDelete}
            />
          </SidebarMenu>
        </SidebarGroupContent>
      </SidebarSection>
      
      <EditBoardDialog />
      <DeleteBoardDialog />
    </div>
  );
};

export default SidebarBoardsSection;
